import React, {FC, useEffect, useState} from 'react';
import DatePicker from "react-datepicker";
import {
    format,
    startOfToday,
    endOfDay
} from "date-fns";
import {useTranslation} from "react-i18next";
import { FormType } from '../../types/types';
import { DeliveryService } from '../../api/DeliveryService';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { autofillDeliveryReturn, setDeliveryDate } from '../../stateReducers/deliverySlice';
import { TFDeliveryService } from '../../api/TFDeliveryService';
import { addDays, endDateFromReturnDate, startDateFromDeliveryDate } from '../utils/stateHelper';
import { getDaysArray } from '../../api/PromotionService';
import BookingService from '../../api/BookingService';
import LoadingIndicator from '../utils/LoadingIndicator';

interface DeliveryDatePickerInterface {
    readonly: boolean,
    deliveryService: DeliveryService,
    setDeliveryDatePickerValid: (b: boolean) => void
}

const DeliveryDatePicker: FC<DeliveryDatePickerInterface> = ({
    readonly,
    deliveryService,
    setDeliveryDatePickerValid
}) => {
    const {t} = useTranslation();

    const formType = useAppSelector((state) => { return state.delivery.formType; });
    const minDeliveryDate = useAppSelector((state) => { return state.delivery.minDeliveryDate!; });
    const maxDeliveryDate = useAppSelector((state) => { return state.delivery.maxDeliveryDate!; });
    const deliveryDate = useAppSelector((state) => { return state.delivery.deliveryDate!; });
    // const returnDate = useAppSelector((state) => { return state.delivery.returnDate; });
    const playStart = useAppSelector((state) => { return state.delivery.playStart!; });
    // const endDate = useAppSelector((state) => { return state.delivery.endDate!; });
    const deliveryType = useAppSelector((state) => { return state.delivery.deliveryType!; });
    const startDate = useAppSelector((state) => { return state.delivery.startDate; });
    const endDate = useAppSelector((state) => { return state.delivery.endDate; });

    const gunOptions = useAppSelector((state) => { return state.gunOptions; });

    const [awaitingResponse, setAwaitingResponse] = useState<boolean>(false);

    const d = useAppDispatch();

    useEffect(() => {
        if (!deliveryDate) {
            d(autofillDeliveryReturn(null));
        }
    }, []);

    function neighboringDatesForDelivery(date1: Date, date2: Date): boolean {
        let start;
        let end;
        if (date1 <= date2) {
            start = date1;
            end = date2
        } else {
            start = date2;
            end = date1;
        }
        console.log("Date 1: " + date1.toString() + "\nDate2: " + date2.toString());

        const businessDaysBetween = getDaysArray(start, end)
            .filter((date, index, originalArray) => {
                // Skip first and last elements
                if (index === 0 || index === originalArray.length - 1) {
                    return true;
                }


                return DeliveryService.isWorkingDate(date);
            });
        return businessDaysBetween.length === 2;
    }

    const [displayDeliveryWarning, setDisplayDeliveryWarning] = useState<boolean>(false);

    // Display a warning if a customer chooses for no buffer day between the delivery date and
    // the playStart
    useEffect(() => {
        if (deliveryService instanceof TFDeliveryService) {
            if (deliveryDate && neighboringDatesForDelivery(deliveryDate, playStart!)) {
                setDisplayDeliveryWarning(true);
            } else {
                setDisplayDeliveryWarning(false);
            }
        }
    }, [deliveryDate]);

    const [viableDeliveryDates, setViableDeliveryDates] = useState<Date[] | null>(null);
    const viableDeliveryStrings = () => {
        return viableDeliveryDates
            ?
            viableDeliveryDates.map((date: Date) => {
                return format(date, "yyyy-MM-dd");

            })
            :
            []
    }

    /**
     * Only call once endDate is calculated
     */
    async function possiblyRemoveDates() {
        setReadyToDisplay(false);
        setAwaitingResponse(true);
        setDeliveryDatePickerValid(false);
        // Have to do for loop rather than forEach because await has to be top level
        const toRemove: Date[] = [];
        for (let i = 0; i < viableDeliveryDates!.length; i++) {
            console.log("Checking possible delivery date: " + viableDeliveryDates![i]);
            const possibleStart = startDateFromDeliveryDate(viableDeliveryDates![i], playStart, deliveryType);
            console.log("Possible start based on delivery date: " + possibleStart);
            const deliveryAvailability = await BookingService.checkAvailabilityForPeriodAndDelivery("transforce", gunOptions.gunType!, gunOptions.quantity!, possibleStart, endDate!, playStart);
            console.log("Delivery availability check for " + viableDeliveryDates![i]);
            console.log(deliveryAvailability);
            if (!(deliveryAvailability.data.delivery == true)) {
                toRemove.push(viableDeliveryDates![i]);
            }
        }
        console.log("To remove: " + toRemove);
        toRemove.forEach((date: Date) => {
            setViableDeliveryDates(viableDeliveryDates!.filter((date1: Date) => {
                const s1 = format(date1, "yyyy-MM-dd");
                const s2 = format(date, "yyyy-MM-dd");
                console.log(`Checking ${s1} against ${s2}`);
                return s1 != s2;
            }));
        });
        const deliveryString = format(deliveryDate, "yyyy-MM-dd");
        console.log("Current delivery string: " + deliveryString);
        if (!(viableDeliveryStrings().includes(deliveryString))) {
            console.log("Need to move delivery date...");
            const newDeliveryDate = DeliveryService.addBusinessDays(deliveryDate, 1);
            d(setDeliveryDate(newDeliveryDate));
            console.log("New delivery date: " + newDeliveryDate);
        }
        setReadyToDisplay(true);
        setAwaitingResponse(false);
        setDeliveryDatePickerValid(true);
    }

    useEffect(() => {
        console.log("viableDeliveryDates: " + viableDeliveryDates);
        if (viableDeliveryDates === null) {
            setViableDeliveryDates(
                getDaysArray(minDeliveryDate, maxDeliveryDate)
                .filter(DeliveryService.isWorkingDate)
                .map((date: Date) => {
                    // date.setHours(2,0,0,0);
                    return date;
                })
            );
        } else if (startDate && endDate && viableDeliveryDates !== null) {
            possiblyRemoveDates();
        }
        // console.log(maxDeliveryDate);
        // console.log(getDaysArray(minDeliveryDate, maxDeliveryDate).filter(DeliveryService.isWorkingDate));
    }, [minDeliveryDate, maxDeliveryDate, startDate, endDate, viableDeliveryDates]);
    const [readyToDisplay, setReadyToDisplay] = useState<boolean>(false);

    if (formType === 'YouthPromo' as FormType) {
        // For the YouthPromo, the date is chosen automatically.
        if (!minDeliveryDate || minDeliveryDate === startOfToday()) {
            // If minDeliveryDate is today, then it ran into an error based on the play date.
            return (
                <div>
                    <p>
                        {t('chooseNewPlayDate')}
                    </p>
                </div>
            );
        } else {
            let dateString = format(minDeliveryDate,"yyy-MM-dd");
            return (
                <div>
                    <p>
                        {dateString}
                    </p>
                </div>
            );
        }
    } else {
        // Default
        return <>
            {displayDeliveryWarning &&
                <p style={{color: 'red', margin: 0}}>
                    {t('delivery.noDeliveryBufferWarning')}
                </p>
            }
            {awaitingResponse &&
                    <LoadingIndicator text={t("loadingRentalPointsLabel")}/>
            }
            {readyToDisplay &&
                <DatePicker
                    className="form-control block w-full px-3 py-1.5 text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out m-0 focus:text-gray-700 focus:bg-white focus:border-orange-200 focus:shadow-none focus:outline-none focus:outline-none"
                    onChange={(date: Date | null) => {
                        d(setDeliveryDate(date));
                    }}
                    selected={deliveryDate}
                    placeholderText={t('delivery.selectDeliveryDatePlaceholder')}
                    minDate={minDeliveryDate}
                    maxDate={maxDeliveryDate}
                    calendarStartDay={1}
                    withPortal
                    portalId="modal-root"
                    // filterDate={DeliveryService.isWorkingDate}
                    filterDate={(date: Date) => {
                        console.log("Filtering...");
                        const dateString = format(date,"yyyy-MM-dd");
                        console.log(dateString);
                        console.log(viableDeliveryDates);
                        return DeliveryService.isWorkingDate(date) && viableDeliveryStrings().includes(dateString);
                    }}
                    readOnly={readonly}
                    dateFormat="dd-MM-yyyy"
                />
            }
        </>;
    }
};

export default DeliveryDatePicker;
